import { useMemo } from 'react';
import { useGate, useUnit } from 'effector-react';
import { Control } from '@unicorn/control';
import type { FormInstance } from '@unicorn/effector-form';
import {
  ObjectsTreeControlGate,
  $objects,
  $isLoading,
  createObjectsTreeFormAdapter,
} from './model';
import type { ObjectType } from './types';

type Props<T> = {
  label?: string;
  form: FormInstance<T>;
  name: string;
  objectTypes: ObjectType[];
  isCollapsible?: boolean;
  isSearchable?: boolean;
  visibleItemsLimit?: number;
  showOnlySelectedBranches?: boolean;
};

/**
 * @example Только комплекс - значение value имеет тип `number[]`:
 * ```tsx
 * <Control.ObjectsTree
 *   name="objects"
 *   form={form}
 *   objectTypes={['complex']}
 * />;
 *
 * @example Только здание - значение value имеет тип `number[]`:
 * ```tsx
 * <Control.ObjectsTree
 *   name="objects"
 *   form={form}
 *   objectTypes={['complex', 'building']}
 * />;
 *
 * @example Только квартиры - значение value имеет тип `number[]`:
 * ```tsx
 * <Control.ObjectsTree
 *   name="objects"
 *   form={form}
 *   objectTypes={['complex', 'building', 'apartment']}
 * />;
 * ```
 *
 * @example Только помещения - значение value имеет тип `number[]`:
 * ```tsx
 * <Control.ObjectsTree
 *   name="objects"
 *   form={form}
 *   objectTypes={['complex', 'building', 'property']}
 * />;
 * ```
 *
 * @example Квартиры и помещения - значение value имеет тип `{ apartments: number[]; properties: number[] }`:
 * ```tsx
 * <Control.ObjectsTree
 *   name="objects"
 *   form={form}
 *   objectTypes={['complex', 'building', 'apartment', 'property']}
 * />;
 * ```
 */
export const ObjectsTreeControl = <T,>({
  objectTypes = [],
  form,
  name,
  ...props
}: Props<T>) => {
  const objectTypesKey = objectTypes.join('|');

  const stableObjectTypes = useMemo(
    () => (objectTypesKey ? (objectTypesKey.split('|') as ObjectType[]) : []),
    [objectTypesKey]
  );

  useGate(ObjectsTreeControlGate, { objectTypes: stableObjectTypes });
  const [objects, isLoading] = useUnit([$objects, $isLoading]);

  const treeForm = useMemo(
    // eslint-disable-next-line effector/no-units-spawn-in-render
    () => createObjectsTreeFormAdapter({ form, name, objectTypes: stableObjectTypes }),
    [form, name, stableObjectTypes]
  );

  return (
    <Control.Tree
      items={objects}
      isLoading={isLoading}
      form={treeForm}
      name={name}
      {...props}
    />
  );
};
